import {
  collection,
  endBefore,
  getDocs,
  limit,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  where
} from "firebase/firestore";
import { useEffect, useRef, useState } from "react";
import {
  putComplaintsData
} from "../Redux/complaints/action";
import { setErrorStatus, setSuccessStatus } from "../Redux/status/action";
import {
  setSelectedTableData,
  setTablePageNumber
} from "../Redux/table/action";
import { exportFiles, updatePenalty } from "../Services/api";
import { getComplaint } from "../Services/database";
import { database } from "../Services/firebase";
import { getFileUrl } from "../Services/storage";
import { isValidArray, isValidObject } from "../Services/validators";
import {
  complaintsQueryBuilder,
  complaintsTableField
} from "../Utils/Constants/Complaints";
import { dateAndTimeConverter } from "../Utils/Constants/Constants";

export const listenerDataLimit = 50;

const getNestedValue = (obj, path) => {
  return path.split(".").reduce((acc, key) => acc && acc[key], obj);
};

function insertIntoSortedArray(arr, element, compareFn) {
  let left = 0;
  let right = arr.length - 1;

  // Binary search using custom comparison
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    const comparison = compareFn(element, arr[mid]);

    if (comparison === 0) {
      arr.splice(mid, 0, element); // Insert at equal position
      return arr;
    } else if (comparison > 0) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }

  // Insert at the correct position
  arr.splice(left, 0, element);
  return arr;
}

export default function useComplaints(props) {
  const complaintsData = useRef(null);
  const complaintsListener = useRef(null);
  const currentCursor = useRef({
    prev: null,
    next: null
  });
  const pageNumber = useRef(1);
  const filterQuery = useRef([]);
  const listeningFilterData = useRef([]);
  const [complaintsLoading, setComplaintsLoading] = useState({
    assets: false,
    pagination: false,
    search: false,
    export: false,
    import: false
  });

  const getComplaintsNextPage = async () => {
    pageNumber.current += 1;
    setTablePageNumber("COMPLAINTS", pageNumber.current);
    currentCursor.current = await getPaginationIncrement({
      isAuth: props.isAuth,
      complaintsData: complaintsData.current,
      complaintsListener: complaintsListener.current,
      complaintsLoading: complaintsLoading,
      currentCursor: currentCursor.current,
      filterQuery: filterQuery.current,
      listeningFilterData: listeningFilterData.current,
      setComplaintsLoading: setComplaintsLoading,
      sort: props.sort,
      pageNumber: pageNumber.current
    });
  };

  const getComplaintsPreviousPage = async () => {
    pageNumber.current -= 1;
    setTablePageNumber("COMPLAINTS", pageNumber.current);
    currentCursor.current = await getPaginationDecrement({
      isAuth: props.isAuth,
      complaintsData: complaintsData.current,
      complaintsListener: complaintsListener.current,
      complaintsLoading: complaintsLoading,
      currentCursor: currentCursor.current,
      filterQuery: filterQuery.current,
      listeningFilterData: listeningFilterData.current,
      setComplaintsLoading: setComplaintsLoading,
      sort: props.sort,
      pageNumber: pageNumber.current
    });
  };

  const getComplaintsSummary = async () => {
    await downloadComplaintsSummary({
      accessToken: props.accessToken,
      complaintsLoading: complaintsLoading,
      filterQuery: filterQuery.current,
      setComplaintsLoading: setComplaintsLoading
    });
  };

  const importPenaltyFile = async (file) => {
    try {
      setComplaintsLoading({ ...complaintsLoading, import: true });

      const response = await updatePenalty(
        props.accessToken,
        file,
        "complaints"
      );

      setSuccessStatus(response.message);

      setComplaintsLoading({ ...complaintsLoading, import: false });
    } catch (error) {
      setComplaintsLoading({ ...complaintsLoading, import: false });
      setErrorStatus(error);
      console.error("There was an error downloading the file:", error);
    }
  };

  // Listener
  const subscribeToComplaints = () => {
    let inequalityFilter = [];
    if (Array.isArray(filterQuery.current)) {
      filterQuery.current.forEach((data, index) => {
        if (["<", "<=", ">", ">=", "not-in", "!="].includes(data)) {
          inequalityFilter.push({
            field: filterQuery.current[index - 1],
            operator: data,
            value: filterQuery.current[index + 1]
          });
        }
      });
    }
    let queryRef = query(collection(database, "complaints"));

    const sortField = complaintsTableField(
      props.sort?.title,
      "sort",
      inequalityFilter.length > 0 ? "local" : "cloud"
    );

    let sortOrder = props.sort?.ascending ? "asc" : "desc";
    if (props.sort?.title === "TURNAROUND TIME") {
      sortOrder = props.sort?.ascending ? "desc" : "asc";
    }

    const baseQueryConstraints = [];
    if (inequalityFilter.length > 0) {
      [...new Set(inequalityFilter.map((filter) => filter.field))].forEach(
        (filter) => {
          baseQueryConstraints.push(orderBy(filter, "desc"));
        }
      );
    } else {
      baseQueryConstraints.push(limit(listenerDataLimit));
    }
    if (sortField && sortOrder && inequalityFilter.length === 0) {
      baseQueryConstraints.push(orderBy(sortField, sortOrder));
    }

    queryRef = query(queryRef, ...baseQueryConstraints);
    filterQuery.current.forEach((value, index) => {
      if ((index + 1) % 3 === 0 && index !== 0) {
        queryRef = query(
          queryRef,
          where(
            filterQuery.current[index - 2],
            filterQuery.current[index - 1],
            value
          )
        );
      }
    });

    return onSnapshot(
      queryRef,
      (dataSnapshot) => {
        let complaints = {};
        let sortedComplaints = [];
        dataSnapshot.forEach((doc) => {
          if (inequalityFilter.length > 0) {
            sortedComplaints = insertIntoSortedArray(
              sortedComplaints,
              {
                documentId: doc.id,
                ...doc.data()
              },
              (a, b) => {
                const aValue = getNestedValue(a, sortField);
                const bValue = getNestedValue(b, sortField);
                if (typeof aValue === "string") {
                  return sortOrder === "asc"
                    ? aValue.localeCompare(bValue, undefined, { numeric: true })
                    : bValue.localeCompare(aValue, undefined, {
                        numeric: true
                      });
                } else if (typeof aValue === "number") {
                  return sortOrder === "asc"
                    ? aValue - bValue
                    : bValue - aValue;
                }
              }
            );
          } else if (inequalityFilter.length === 0) {
            complaints[doc.id] = {
              ...doc.data(),
              documentId: doc.id
            };
          }
        });

        sortedComplaints
          .slice(
            (pageNumber.current - 1) * listenerDataLimit,
            pageNumber.current * listenerDataLimit + 1
          )
          .forEach((complaint) => {
            complaints[complaint.documentId] = {
              ...complaint
            };
          });

        currentCursor.current.next =
          dataSnapshot.docs[dataSnapshot.docs.length - 1];

        if (
          complaintsData.current === null ||
          JSON.stringify(listeningFilterData.current) !==
            JSON.stringify(filterQuery.current)
        ) {
          listeningFilterData.current = filterQuery.current;
          complaintsData.current = complaints;
        } else {
          complaintsData.current = complaints;
        }
        putComplaintsData(complaints, filterQuery.current);
        // getAllComplaintsCount({
        //   filter: filterQuery.current
        // });
      },
      (error) => {
        console.error(error, "from complaints listener");
        setErrorStatus(error);
      }
    );
  };

  useEffect(() => {
    if (props.isAuth && typeof complaintsListener.current === "function") {
      complaintsListener.current();
      complaintsListener.current = subscribeToComplaints();
      pageNumber.current = 1;
      setTablePageNumber("COMPLAINTS", pageNumber.current);
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.sort]);

  useEffect(() => {
    const query = complaintsQueryBuilder(props.filterData, props.locationData);
    filterQuery.current = query;
    if (
      typeof complaintsListener.current === "function" &&
      props.isAuth &&
      JSON.stringify(listeningFilterData.current) !==
        JSON.stringify(filterQuery.current)
    ) {
      complaintsData.current = null;
      listeningFilterData.current = filterQuery.current;
      complaintsListener.current();
      complaintsListener.current = subscribeToComplaints();
      pageNumber.current = 1;
      setTablePageNumber("COMPLAINTS", pageNumber.current);
      // getAllComplaintsCount({
      //   filter: filterQuery.current
      // });
    }
    // eslint-disable-next-line
  }, [props.filterData, props.locationData]);

  useEffect(() => {
    if (
      !props.selectedComplaint?.fileSrc ||
      !props.selectedComplaint?.closure
    ) {
      getComplaintsAssets({
        selectedData: props.selectedComplaint,
        setComplaintsLoading: setComplaintsLoading,
        complaintsLoading: complaintsLoading
      });
    }
    // eslint-disable-next-line
  }, [props.selectedComplaint]);

  useEffect(() => {
    const fetchSearchedComplaint = async () => {
      setComplaintsLoading((prevState) => ({ ...prevState, search: true }));

      const searchResult = await getSearchedComplaint({
        complaintId: props.searchKey,
        complaintsData
      });
      if (isValidObject(searchResult)) {
        putComplaintsData(
          { [searchResult.documentId]: searchResult },
          filterQuery.current
        );
      } else {
        putComplaintsData({}, filterQuery.current);
      }

      setComplaintsLoading((prevState) => ({ ...prevState, search: false }));
    };

    if (props.searchKey?.length === 8 && props.isAuth) {
      fetchSearchedComplaint();
    } else {
      if (props.isAuth) {
        putComplaintsData(complaintsData.current, filterQuery.current);
      }
    }
    // eslint-disable-next-line
  }, [props.isAuth, props.searchKey]);

  useEffect(() => {
    if (props.isAuth === true && complaintsListener.current === null) {
      complaintsListener.current = subscribeToComplaints();
    } else if (
      typeof complaintsListener.current === "function" &&
      props.isAuth === false
    ) {
      complaintsListener.current();
      complaintsListener.current = null;
    }
    // eslint-disable-next-line
  }, [props.isAuth]);

  return {
    getComplaintsNextPage,
    getComplaintsPreviousPage,
    complaintsLoading,
    getComplaintsSummary,
    importPenaltyFile
  };
}

const getComplaintsAssets = async (props) => {
  try {
    props.setComplaintsLoading({ ...props.complaintsLoading, assets: true });

    if (!isValidObject(props.selectedData)) {
      props.setComplaintsLoading({ ...props.complaintsLoading, assets: false });
      return;
    }

    const processFilesConcurrently = async (files) => {
      if (!isValidArray(files)) {
        return { images: [], audio: null };
      }
      const results = await Promise.all(
        files.map(async (fileId) => {
          try {
            return await getFileUrl(fileId);
          } catch (error) {
            return {
              type: null,
              url: null
            };
          }
        })
      );
      const images = results
        .filter((file) => file.type === "image")
        .map((file) => file.url);
      const audio = results.find((file) => file.type === "audio")?.url || null;

      return { images, audio };
    };

    const complaintAssets = await processFilesConcurrently(
      props.selectedData.assets
    );

    if (isValidArray(props.selectedData.latestUpdate)) {
      await Promise.all(
        props.selectedData.latestUpdate.map(async (update) => {
          const proof = update.proof || [];
          const assets = await processFilesConcurrently(proof);

          update.assets = {
            images: assets.images,
            audio: assets.audio
          };
        })
      );
    }

    const closureAssets = isValidArray(props.selectedData.closure?.proof)
      ? await processFilesConcurrently(props.selectedData.closure.proof)
      : { images: [], audio: null };

    setSelectedTableData("COMPLAINTS", {
      ...props.selectedData,
      fileSrc: complaintAssets,
      closure: {
        ...props.selectedData.closure,
        fileSrc: closureAssets
      }
    });

    props.setComplaintsLoading({ ...props.complaintsLoading, assets: false });
  } catch (error) {
    props.setComplaintsLoading({ ...props.complaintsLoading, assets: false });
    console.error("getComplaintsAssets", error);
  }
};

const downloadComplaintsSummary = async (props) => {
  try {
    props.setComplaintsLoading({ ...props.complaintsLoading, export: true });

    const blob = await exportFiles(
      props.accessToken,
      props.filterQuery,
      "complaints"
    );

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    link.setAttribute(
      "download",
      `Complaints Report @${dateAndTimeConverter(Date.now(), "Time").replace(
        ":",
        "-"
      )},
        ${dateAndTimeConverter(Date.now(), "cardDate").replace(":", "-")}.xlsx`
    );
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    props.setComplaintsLoading({ ...props.complaintsLoading, export: false });
  } catch (error) {
    props.setComplaintsLoading({ ...props.complaintsLoading, export: false });
    console.error("There was an error downloading the file:", error);
  }
};

const getSearchedComplaint = async (props) => {
  try {
    const existData = props.complaintsData?.[props.complaintId];
    if (isValidObject(existData)) {
      return existData;
    } else {
      const searchComplaint = await getComplaint(props.complaintId);
      return isValidObject(searchComplaint) ? searchComplaint : {};
    }
  } catch (error) {
    console.error("getSearchedComplaint error:", error);
    return {};
  }
};

const getPaginationIncrement = async (props) => {
  if (
    typeof props.complaintsListener !== "function" ||
    props.complaintsData === null ||
    !props.isAuth ||
    JSON.stringify(props.listeningFilterData) !==
      JSON.stringify(props.filterQuery)
  ) {
    return;
  }
  props.setComplaintsLoading({ ...props.complaintsLoading, pagination: true });
  if (!props.currentCursor || !props.pageNumber) {
    return props.setComplaintsLoading({
      ...props.complaintsLoading,
      pagination: false
    });
  }
  let inequalityFilter = [];
  if (Array.isArray(props.listeningFilterData)) {
    props.listeningFilterData.forEach((data, index) => {
      if (["<", "<=", ">", ">=", "not-in", "!="].includes(data)) {
        inequalityFilter.push({
          field: props.listeningFilterData[index - 1],
          operator: data,
          value: props.listeningFilterData[index + 1]
        });
      }
    });
  }

  let queryRef = query(collection(database, "complaints"));

  const sortField = complaintsTableField(
    props.sort?.title,
    "sort",
    inequalityFilter.length > 0 ? "local" : "cloud"
  );

  let sortOrder = props.sort?.ascending ? "asc" : "desc";
  if (props.sort?.title === "TURNAROUND TIME") {
    sortOrder = props.sort?.ascending ? "desc" : "asc";
  }

  const baseQueryConstraints = [];
  if (sortField && sortOrder && inequalityFilter.length === 0) {
    baseQueryConstraints.push(orderBy(sortField, sortOrder));
  }
  if (inequalityFilter.length > 0) {
    [...new Set(inequalityFilter.map((filter) => filter.field))].forEach(
      (filter) => {
        baseQueryConstraints.push(orderBy(filter, "desc"));
      }
    );
  } else {
    baseQueryConstraints.push(startAfter(props.currentCursor.next));
    baseQueryConstraints.push(limit(listenerDataLimit));
  }

  queryRef = query(queryRef, ...baseQueryConstraints);
  props.listeningFilterData.forEach((value, index) => {
    if ((index + 1) % 3 === 0 && index !== 0) {
      queryRef = query(
        queryRef,
        where(
          props.listeningFilterData[index - 2],
          props.listeningFilterData[index - 1],
          value
        )
      );
    }
  });
  const querySnapshot = await getDocs(queryRef);
  let sortedComplaints = [];
  let complaints = {};
  querySnapshot.forEach((doc) => {
    if (inequalityFilter.length > 0) {
      sortedComplaints = insertIntoSortedArray(
        sortedComplaints,
        {
          documentId: doc.id,
          ...doc.data()
        },
        (a, b) => {
          const aValue = getNestedValue(a, sortField);
          const bValue = getNestedValue(b, sortField);
          if (typeof aValue === "string") {
            return sortOrder === "asc"
              ? aValue.localeCompare(bValue, undefined, { numeric: true })
              : bValue.localeCompare(aValue, undefined, { numeric: true });
          } else if (typeof aValue === "number") {
            return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
          }
        }
      );
    } else if (inequalityFilter.length === 0) {
      complaints[doc.id] = {
        ...doc.data(),
        documentId: doc.id
      };
    }
  });

  sortedComplaints
    .slice(
      (props.pageNumber - 1) * listenerDataLimit,
      props.pageNumber * listenerDataLimit + 1
    )
    .forEach((complaint) => {
      complaints[complaint.documentId] = {
        ...complaint
      };
    });
  props.complaintsData = complaints;

  props.setComplaintsLoading({ ...props.complaintsLoading, pagination: false });
  putComplaintsData(props.complaintsData, props.filterQuery);
  return {
    next: querySnapshot.docs[querySnapshot.docs.length - 1],
    prev: querySnapshot.docs[0]
  };
};

const getPaginationDecrement = async (props) => {
  if (
    typeof props.complaintsListener !== "function" ||
    props.complaintsData === null ||
    !props.isAuth ||
    JSON.stringify(props.listeningFilterData) !==
      JSON.stringify(props.filterQuery)
  ) {
    return;
  }

  props.setComplaintsLoading({ ...props.complaintsLoading, pagination: true });

  if (!props.currentCursor || !props.pageNumber) {
    return props.setComplaintsLoading({
      ...props.complaintsLoading,
      pagination: false
    });
  }

  let inequalityFilter = [];
  if (Array.isArray(props.listeningFilterData)) {
    props.listeningFilterData.forEach((data, index) => {
      if (["<", "<=", ">", ">=", "not-in", "!="].includes(data)) {
        inequalityFilter.push({
          field: props.listeningFilterData[index - 1],
          operator: data,
          value: props.listeningFilterData[index + 1]
        });
      }
    });
  }

  let queryRef = query(collection(database, "complaints"));

  const sortField = complaintsTableField(
    props.sort?.title,
    "sort",
    inequalityFilter.length > 0 ? "local" : "cloud"
  );

  let sortOrder = props.sort?.ascending ? "asc" : "desc";
  if (props.sort?.title === "TURNAROUND TIME") {
    sortOrder = props.sort?.ascending ? "desc" : "asc";
  }

  const baseQueryConstraints = [];
  if (sortField && sortOrder && inequalityFilter.length === 0) {
    baseQueryConstraints.push(orderBy(sortField, sortOrder));
  }
  if (inequalityFilter.length > 0) {
    [...new Set(inequalityFilter.map((filter) => filter.field))].forEach(
      (filter) => {
        baseQueryConstraints.push(orderBy(filter, "desc"));
      }
    );
  } else {
    baseQueryConstraints.push(endBefore(props.currentCursor.prev));
    baseQueryConstraints.push(limitToLast(listenerDataLimit));
  }

  queryRef = query(queryRef, ...baseQueryConstraints);
  props.listeningFilterData.forEach((value, index) => {
    if ((index + 1) % 3 === 0 && index !== 0) {
      queryRef = query(
        queryRef,
        where(
          props.listeningFilterData[index - 2],
          props.listeningFilterData[index - 1],
          value
        )
      );
    }
  });
  const querySnapshot = await getDocs(queryRef);
  let sortedComplaints = [];
  let complaints = {};
  querySnapshot.forEach((doc) => {
    if (inequalityFilter.length > 0) {
      sortedComplaints = insertIntoSortedArray(
        sortedComplaints,
        {
          documentId: doc.id,
          ...doc.data()
        },
        (a, b) => {
          const aValue = getNestedValue(a, sortField);
          const bValue = getNestedValue(b, sortField);
          if (typeof aValue === "string") {
            return sortOrder === "asc"
              ? aValue.localeCompare(bValue, undefined, { numeric: true })
              : bValue.localeCompare(aValue, undefined, { numeric: true });
          } else if (typeof aValue === "number") {
            return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
          }
        }
      );
    } else if (inequalityFilter.length === 0) {
      complaints[doc.id] = {
        ...doc.data(),
        documentId: doc.id
      };
    }
  });

  sortedComplaints
    .slice(
      (props.pageNumber - 1) * listenerDataLimit,
      props.pageNumber * listenerDataLimit + 1
    )
    .forEach((complaint) => {
      complaints[complaint.documentId] = {
        ...complaint
      };
    });
  props.complaintsData = complaints;

  props.setComplaintsLoading({ ...props.complaintsLoading, pagination: false });
  putComplaintsData(props.complaintsData, props.filterQuery);
  return {
    next: querySnapshot.docs[querySnapshot.docs.length - 1],
    prev: querySnapshot.docs[0]
  };
};
